/**
 * Module dependencies.
 */

import { Container } from 'src/components/core/layout/container';
import { Image } from 'src/components/core/image';
import { Text } from 'src/components/core/text';
import { media } from 'src/styles/utils/media';
import { useBreakpoint } from '@untile/react-components';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  items: {
    description: string;
    image: string;
    title: string;
  }[];
  title: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  background: var(--gradient-primary);
  color: var(--color-white);
  padding: var(--vertical-spacing) 0;
  position: relative;
  text-align: center;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Text).attrs({
  variant: 'display2'
})`
  --icon-cards-title-margin-bottom: 40px;

  margin-bottom: var(--icon-cards-title-margin-bottom);

  ${media.min.sm`
    --icon-cards-title-margin-bottom 60px;
  `}

  ${media.min.lg`
    --icon-cards-title-margin-bottom 80px;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  --grid-gap: 3rem;

  align-items: flex-start;
  display: grid;
  grid-gap: var(--grid-gap);
  grid-template-columns: 1fr;

  ${media.min.ms`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min.lg`
    --grid-gap: 5rem;

    grid-template-columns: repeat(3, 1fr);
  `}
`;

/**
 * `Card` styled component.
 */

const Card = styled.div`
  display: grid;
  flex: 1;
  grid-gap: 24px;

  ${media.min.sm`
    flex: 0 0 calc(50% - var(--grid-gap));
  `}

  ${media.min.md`
    flex-basis: calc(33.33% - var(--grid-gap));
    grid-gap: 32px;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  height: 136px;
  margin: 0 auto;
  position: relative;
  width: 136px;
`;

/**
 * `IconCardsGrid` component.
 */

export function IconCardsGrid(props: Props) {
  const { items, title } = props;
  const isDesktop = useBreakpoint('md');
  const isTablet = useBreakpoint('sm');
  const gridItemsPerRow = (isDesktop && 3) || (isTablet && 2) || 1;

  return (
    <Wrapper>
      <Container>
        <Title
          as={'h2'}
          data-scroll={'fade-in-up'}
          data-scroll-offset={'20%'}
        >
          {title}
        </Title>

        <Grid>
          {items.map(({ description, image, title }, index) => (
            <Card
              data-scroll={'fade-in-up'}
              data-scroll-animation-delay={((index % gridItemsPerRow) + 1) * 200}
              data-scroll-offset={'20%'}
              key={index}
            >
              {image && (
                <ImageWrapper>
                  <Image
                    alt={title}
                    fill
                    src={image}
                  />
                </ImageWrapper>
              )}

              <Text
                as={'h3'}
                block
                variant={'h3'}
              >
                {title}
              </Text>

              <Text block>{description}</Text>
            </Card>
          ))}
        </Grid>
      </Container>
    </Wrapper>
  );
}
